package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.kyd.events.MouseEvent

/**
 * Defines how an [Actor] moves etc.
 *
 * An [Actor]'s behaviour can change, the classic example is Pac-Man ghosts :
 * At first they are trying to catch Pac-Man, but when a power-pellet
 * is eaten, they change behaviour and flee.
 *
 * You could implement this ghost-like behaviour with a big `if` in a single [Behaviour],
 * or have one [Behaviour] for chasing, and another for fleeing. Set [Actor.behaviour]
 * when a power-pellet is eaten, and reset it when it wears off.
 * I prefer the latter, YMMV.
 */
interface Behaviour : Customisable, Ticks {

    fun onEnteredStage(actor: Actor) {}

    fun onActStarted(actor: Actor) {}

    fun onExitedStage(actor: Actor, oldStage : Stage) {}

}

/**
 * Many Behaviours are never interact with mouse presses, and if we checked every single Role when
 * a mouse was pressed/released, this would be quite expensive
 * (because to convert from screen coordinates to the Actor's local coordinates is non-trivial).
 *
 * Therefore, when a mouse events, are only sent to [Behaviour]s which also implement [MouseBehaviour].
 */
interface MouseBehaviour : Behaviour {

    /**
     * Which implementation of [Touching] should be used for mouse events?
     * The default is [TouchingPixel.withinBoundsAndPixel].
     *
     * This is a reasonable choice, except for buttons which are plain [Label]s,
     * without a background.
     *
     * For appearances with a drop-shadow, you may want to use a different instance of [TouchingPixel]
     * with a higher threshold.
     */
    val touchTest: Touching get() = TouchingPixel.withinBoundsAndPixel

    /**
     * When set to `false` [onMouseClicked] will NOT be called.
     * In some cases, you may want to keep this value `true`, even when the behaviour is _inactive_.
     * For example, an _inactive_ button may keep [isMouseEnabled] == `true`, and inside [onMouseClicked]
     * play a sound to indicate the click did nothing.
     * In such cases, the return value from [onMouseClicked] should be `true`, as the event has been handled.
     */
    val isMouseEnabled: Boolean
        get() = true

    /**
     * A mouse-click has occurred.
     * [touchTest] has already returned `true`, so you can be sure that the click is near the Actor.
     * Therefore, for a simple button, [MouseEvent.local] will be ignored.
     * But for a `slider` we want to know how far along the slider;
     * in which case we will only be concerned with the x component for a horizontal slider.
     *
     * @return `true` if the click has been handled; no other [MouseBehaviour] will receive the click event.
     */
    fun onMouseClicked(actor: Actor, event: MouseEvent): Boolean = false

    fun onMousePressed(actor: Actor, event: MouseEvent): Boolean = false

    /**
     * Only ever called when [MouseEvent.capture] is called in a previous [onMousePressed] event.
     */
    fun onMouseReleased(actor: Actor, event: MouseEvent) {}

    /**
     * Only ever called when [MouseEvent.capture] is called in a previous [onMousePressed] event.
     */
    fun onMouseDragged(actor: Actor, event: MouseEvent) {}

}

object NoBehaviour : Behaviour {
    override fun toString() = "NoBehaviour"
}

